// Admin → Reporting
//
// Configuration for the automatic Court Supervisors report email:
// every night at 11:30pm (Australia/Sydney) the backend collates the
// day's game reports (forfeits, tech fouls, injuries, general info)
// and emails them to the recipients chosen here. Recipients are picked
// from the people who have access to the platform (anyone assigned a
// security profile).

import React, { useEffect, useState, useCallback } from 'react';
import { auditFetch } from '../auditFetch';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function AdminReporting({ canEdit }) {
  const [autoSendEnabled, setAutoSendEnabled] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [pickerValue, setPickerValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [cfgRes, usersRes] = await Promise.all([
        auditFetch(`${API_BASE}/settings/reporting`),
        auditFetch(`${API_BASE}/reporting/platform-users`),
      ]);
      const cfg = await cfgRes.json().catch(() => ({}));
      if (!cfgRes.ok) throw new Error(cfg.message || `HTTP ${cfgRes.status}`);
      setAutoSendEnabled(!!cfg.autoSendEnabled);
      setRecipients(Array.isArray(cfg.recipients) ? cfg.recipients : []);
      if (usersRes.ok) setPlatformUsers(await usersRes.json());
    } catch (e) {
      setError(e.message || 'Failed to load reporting settings');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const userLabel = (email) => {
    const u = platformUsers.find(p => p.email.toLowerCase() === email.toLowerCase());
    return u && u.name ? `${u.name} (${email})` : email;
  };

  const addRecipient = (email) => {
    if (!email) return;
    setRecipients(prev => prev.some(r => r.toLowerCase() === email.toLowerCase()) ? prev : [...prev, email]);
    setPickerValue('');
    setSuccess('');
  };

  const removeRecipient = (email) => {
    setRecipients(prev => prev.filter(r => r.toLowerCase() !== email.toLowerCase()));
    setSuccess('');
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await auditFetch(`${API_BASE}/settings/reporting`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoSendEnabled, recipients }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || `HTTP ${res.status}`);
      setSuccess('Reporting settings saved.');
    } catch (e) {
      setError(e.message || 'Failed to save');
    }
    setSaving(false);
  };

  const available = platformUsers.filter(u => !recipients.some(r => r.toLowerCase() === u.email.toLowerCase()));

  return (
    <div className="admin-section">
      <h2 style={{ marginTop: 0 }}>Reporting</h2>
      <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: '1rem' }}>
        The <strong>Court Supervisors</strong> report collates every game report entered on the Game
        page (forfeits, tech fouls, injuries, general info). When the automatic send is enabled, the
        day's collated report is emailed every night at <strong>11:30pm</strong> to the people below.
        The same report can be run on demand from Reports → Court Supervisors.
      </p>

      {loading ? <p>Loading…</p> : (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: '1rem' }}>
            <input
              type="checkbox"
              checked={autoSendEnabled}
              disabled={!canEdit}
              onChange={e => { setAutoSendEnabled(e.target.checked); setSuccess(''); }}
            />
            Automatically email the Court Supervisors report daily at 11:30pm
          </label>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>Recipients</label>
            {recipients.length === 0 && (
              <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 6px' }}>
                No recipients yet{autoSendEnabled ? ' — the email will not send until at least one is added.' : '.'}
              </p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {recipients.map(email => (
                <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 999, padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}>
                  {userLabel(email)}
                  {canEdit && (
                    <button onClick={() => removeRecipient(email)} title="Remove recipient" style={{ background: 'none', border: 'none', color: '#c62828', cursor: 'pointer', fontWeight: 700, padding: 0 }}>✕</button>
                  )}
                </span>
              ))}
            </div>
            {canEdit && (
              <select
                value={pickerValue}
                onChange={e => addRecipient(e.target.value)}
                style={{ padding: '0.45rem', border: '1px solid #ccc', borderRadius: 4, minWidth: 340 }}
              >
                <option value="">Add a person with platform access…</option>
                {available.map(u => (
                  <option key={u.email} value={u.email}>
                    {u.name ? `${u.name} — ` : ''}{u.email} ({u.profileName})
                  </option>
                ))}
              </select>
            )}
          </div>

          {error && <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', padding: '0.5rem 0.75rem', borderRadius: 4, marginBottom: '0.75rem' }}>{error}</div>}
          {success && <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', color: '#1b5e20', padding: '0.5rem 0.75rem', borderRadius: 4, marginBottom: '0.75rem' }}>{success}</div>}

          {canEdit ? (
            <button onClick={save} disabled={saving} className="btn-submit" style={{ background: '#2e7d32', color: 'white', border: 'none', borderRadius: 4, padding: '0.5rem 1.25rem', cursor: 'pointer', fontWeight: 700 }}>
              {saving ? 'Saving…' : 'Save reporting settings'}
            </button>
          ) : (
            <p style={{ color: '#888', fontSize: '0.85rem' }}>You have view-only access to Reporting settings.</p>
          )}
        </>
      )}
    </div>
  );
}
